/** Global admission precedes the field's active timer; HTTP retains its own limits. */
import { onPresentationTransportCapacity, presentationTransportCanAdmit } from './chatPresentationTransport'
type Cost = { entries: number; characters: number }
type Job = Cost & { locale: string; run: () => Promise<void> }
const MAX_ENTRIES = 6000 // Same observed-field bound as chatDisplayRegistry.
const MAX_CHARACTERS = MAX_ENTRIES * 4000 // Endpoint maximum per segment; UTF-16 code units.
const lanes = new Map<string, Job[]>()
let active = 0
let exclusive = false
let queuedEntries = 0
let queuedCharacters = 0
let scheduled = false
const isLarge = (job: Cost) => job.entries > 32 || job.characters > 6000

function wake() {
  if (scheduled) return
  scheduled = true
  queueMicrotask(() => { scheduled = false; pump() })
}

function remove(owner: string, job: Job) {
  const lane = lanes.get(owner)
  const index = lane?.indexOf(job) ?? -1
  if (index < 0) return false
  lane!.splice(index, 1)
  queuedEntries -= job.entries
  queuedCharacters -= job.characters
  if (!lane!.length) lanes.delete(owner)
  return true
}

function pump() {
  while (active < 2 && !exclusive && lanes.size) {
    const [owner, lane] = lanes.entries().next().value!
    const first = lane[0]
    const large = isLarge(first)
    // Reserve the next turn for a large field: drain, never starve it.
    if (large && active) return
    if (!presentationTransportCanAdmit(large)) return
    const batch: Job[] = []
    let entries = 0
    let characters = 0
    for (const job of [...lane]) {
      if (job.locale !== first.locale) continue
      if (batch.length && (large || entries + job.entries > 32 || characters + job.characters > 6000)) continue
      batch.push(job)
      entries += job.entries
      characters += job.characters
      remove(owner, job)
      if (large) break
    }
    // Round-robin owners, retaining FIFO order within their pending lane.
    const remainder = lanes.get(owner)
    if (remainder) { lanes.delete(owner); lanes.set(owner, remainder) }
    active++
    exclusive = large
    void Promise.allSettled(batch.map(job => job.run())).finally(() => {
      active--
      if (large) exclusive = false
      wake()
    })
  }
}

export function admitChatPresentation<T>(
  owner: string, locale: string, cost: Cost, signal: AbortSignal, run: () => Promise<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (signal.aborted || cost.entries > MAX_ENTRIES - queuedEntries ||
      cost.characters > MAX_CHARACTERS - queuedCharacters) {
      reject(new Error('Presentation admission unavailable'))
      return
    }
    const cancel = () => {
      if (remove(owner, job)) { reject(new Error('Presentation admission obsolete')); wake() }
      signal.removeEventListener('abort', cancel)
    }
    const job: Job = {
      ...cost, locale,
      run: async () => {
        signal.removeEventListener('abort', cancel)
        if (signal.aborted) { reject(new Error('Presentation admission obsolete')); return }
        try { resolve(await run()) } catch (error) { reject(error) }
      },
    }
    const lane = lanes.get(owner) ?? []
    lane.push(job)
    lanes.set(owner, lane)
    queuedEntries += cost.entries
    queuedCharacters += cost.characters
    signal.addEventListener('abort', cancel, { once: true })
    wake()
  })
}

onPresentationTransportCapacity(wake)
